/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { User } from '../types';
import { generateUsername, CARGOS } from '../data/mockData';
import { UserPlus, CheckCircle, Info, Keyboard, PenTool, Upload, RefreshCw } from 'lucide-react';
import { motion } from 'motion/react';
import { compressImage } from '../utils/imageCompressor';

interface CreateUserProps {
  onUserCreated: (newUser: User) => void;
  existingUsers: User[];
  currentUser: User | null;
  allGlobalUsers?: User[];
}

export default function CreateUser({ onUserCreated, existingUsers, currentUser, allGlobalUsers }: CreateUserProps) {
  const [nombres, setNombres] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [cedula, setCedula] = useState('');
  const [cargo, setCargo] = useState(CARGOS[0]);
  const [esAdmin, setEsAdmin] = useState(false);
  const [modulosPermitidos, setModulosPermitidos] = useState<string[]>(['registro_ordenes']);
  const [previewUsername, setPreviewUsername] = useState('');
  const [successInfo, setSuccessInfo] = useState<{ username: string; cedula: string } | null>(null);
  const [error, setError] = useState('');

  // Signature States
  const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw');
  const [signatureBase64, setSignatureBase64] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Check if cargo requires a signature (All roles are mandatory to have a signature)
  const requiresSignature = true;

  // Force esAdmin to false if the creator is not Admin
  useEffect(() => {
    if (!currentUser?.esAdmin) {
      setEsAdmin(false);
    }
  }, [currentUser, cargo]);

  // Update proposed username preview
  useEffect(() => {
    const cleanN = nombres.trim();
    const cleanA = apellidos.trim();
    if (cleanN && cleanA) {
      const norm = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
      const firstN = norm(cleanN.split(/\s+/)[0] || '');
      const firstA = norm(cleanA.split(/\s+/)[0] || '');
      const base = `${firstN}.${firstA}`;
      let proposed = base;
      let suffix = 1;

      const usersToVerify = allGlobalUsers || existingUsers || [];

      let iterations = 0;
      // Check collision in usersToVerify to display the exact username they will inherit
      while (iterations < 100 && Array.isArray(usersToVerify) && usersToVerify.some(u => u && typeof u === 'object' && (u.username || '').toUpperCase() === proposed)) {
        proposed = `${base}${suffix}`;
        suffix++;
        iterations++;
      }
      setPreviewUsername(proposed);
    } else {
      setPreviewUsername('');
    }
  }, [nombres, apellidos, existingUsers, allGlobalUsers]);

  // Canvas drawing handlers
  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    if ('touches' in e) {
      if (e.touches.length === 0) return { x: 0, y: 0 };
      // Handle page scroll scaling
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b'; // slate-800
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    saveCanvasState();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureBase64('');
  };

  const saveCanvasState = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Check if the canvas is blank or drawn on
    const dataUrl = canvas.toDataURL('image/png');
    const compressed = await compressImage(dataUrl, 300, 100);
    setSignatureBase64(compressed);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        if (typeof reader.result === 'string') {
          const compressed = await compressImage(reader.result, 300, 100);
          setSignatureBase64(compressed);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessInfo(null);

    const cleanNombres = nombres.trim();
    const cleanApellidos = apellidos.trim();

    // Validation strings
    if (!cleanNombres) {
      setError('Por favor digite los Nombres.');
      return;
    }
    if (!cleanApellidos) {
      setError('Por favor digite los Apellidos.');
      return;
    }
    if (!cedula.trim()) {
      setError('Por favor digite la Cédula.');
      return;
    }
    if (!/^\d+$/.test(cedula.trim())) {
      setError('La Cédula debe contener únicamente números.');
      return;
    }

    if (requiresSignature && !signatureBase64) {
      setError('La firma digital es requerida y obligatoria para todos los cargos de la plataforma. Por favor dibuje o cargue una firma.');
      return;
    }

    // Generate username: primer nombre y primer apellido separado por un punto en mayúscula
    const norm = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    const firstN = norm(cleanNombres.split(/\s+/)[0] || '');
    const firstA = norm(cleanApellidos.split(/\s+/)[0] || '');
    const baseUsername = `${firstN}.${firstA}`;
    let calculatedUsername = baseUsername;
    let suffix = 1;

    const usersToVerify = allGlobalUsers || existingUsers || [];

    let iterations = 0;
    // Automatically resolve collisions inside usersToVerify by appending a numeric suffix
    while (iterations < 100 && Array.isArray(usersToVerify) && usersToVerify.some(u => u && typeof u === 'object' && (u.username || '').toUpperCase() === calculatedUsername)) {
      calculatedUsername = `${baseUsername}${suffix}`;
      suffix++;
      iterations++;
    }
    
    // Also save nombreCompleto in UPPERCASE as requested
    const finalNombreCompleto = `${cleanNombres} ${cleanApellidos}`.toUpperCase();

    // Check if user already exists based strictly on unique Cedula
    const isCedulaDuplicate = existingUsers.some(u => u.cedula === cedula.trim());

    if (isCedulaDuplicate) {
      setError('Ya existe un usuario registrado con esta Cédula en el sistema.');
      return;
    }

    const newUser: User = {
      id: `user-${Date.now()}`,
      nombreCompleto: finalNombreCompleto,
      cedula: cedula.trim(),
      cargo,
      username: calculatedUsername,
      estado: 'ACTIVO',
      esAdmin,
      modulosPermitidos: esAdmin 
        ? ['registro_ordenes', 'disposicion_productos', 'dashboard_productividad', 'gestion_usuarios']
        : modulosPermitidos,
      firma: requiresSignature ? signatureBase64 : undefined,
      companyId: currentUser?.companyId || 'company-insumedic'
    };

    onUserCreated(newUser);
    
    // Save success information to present in the UI
    setSuccessInfo({
      username: calculatedUsername,
      cedula: cedula.trim()
    });

    // Reset controls
    setNombres('');
    setApellidos('');
    setCedula('');
    setCargo(CARGOS[0]);
    setEsAdmin(false);
    setModulosPermitidos(['registro_ordenes']);
    setSignatureBase64('');
    if (canvasRef.current) {
      clearCanvas();
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-md shadow-slate-100/40">
      <div className="flex items-center space-x-2.5 mb-5 border-b border-slate-100 pb-4">
        <div className="p-2 bg-blue-50 rounded-lg text-[#1C4F8A]">
          <UserPlus className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-sans font-extrabold text-slate-800 text-base leading-tight">
            Crear Nuevo Usuario
          </h2>
          <p className="text-xs text-slate-500 font-sans mt-0.5">
            Registro administrativo para personal asistencial de INSUMEDIC S.A.S
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" id="form-create-user">
        {error && (
          <div className="bg-red-50 text-red-700 text-xs p-3 rounded-lg border border-red-100 font-sans animate-pulse">
            {error}
          </div>
        )}

        {successInfo && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 text-emerald-900 text-xs p-4 rounded-xl border border-emerald-200 font-sans space-y-2"
          >
            <div className="flex items-center font-bold text-emerald-800">
              <CheckCircle className="h-4.5 w-4.5 mr-2 text-emerald-600" />
              ¡Usuario Creado Exitosamente!
            </div>
            <div className="pl-6 space-y-1 text-slate-700">
              <p>El usuario fue registrado con los siguientes credenciales:</p>
              <p>🔑 <strong className="text-slate-900">Usuario:</strong> <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-emerald-100 text-emerald-700 font-bold">{successInfo.username}</span></p>
              <p>🔒 <strong className="text-slate-900">Contraseña:</strong> <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-emerald-100 text-emerald-700 font-bold">{successInfo.cedula}</span> <span className="text-[10px] text-slate-550">(Cédula del usuario)</span></p>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" htmlFor="input-nombres">
              Nombres
            </label>
            <input
              type="text"
              required
              value={nombres}
              onChange={(e) => {
                setNombres(e.target.value);
                if (successInfo) setSuccessInfo(null);
                if (error) setError('');
              }}
              placeholder="Ej. Juan Pablo"
              className="w-full h-10 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-sans text-slate-800"
              id="input-nombres"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5" htmlFor="input-apellidos">
              Apellidos
            </label>
            <input
              type="text"
              required
              value={apellidos}
              onChange={(e) => {
                setApellidos(e.target.value);
                if (successInfo) setSuccessInfo(null);
                if (error) setError('');
              }}
              placeholder="Ej. Castro Perez"
              className="w-full h-10 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-sans text-slate-800"
              id="input-apellidos"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
            Cédula de Ciudadanía
          </label>
          <input
            type="text"
            required
            value={cedula}
            onChange={(e) => {
              setCedula(e.target.value);
              if (successInfo) setSuccessInfo(null);
              if (error) setError('');
            }}
            placeholder="Ej. 1012345678"
            className="w-full h-10 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-sans text-slate-800"
            id="input-cedula"
          />
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
            Cargo / Rol en la Empresa
          </label>
          <select
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            className="w-full h-10 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all font-sans text-slate-800"
            id="select-cargo"
          >
            {CARGOS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic Signature Area if role requires the badge signature */}
        {requiresSignature && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <span className="text-xs font-bold text-slate-850 flex items-center gap-1.5">
                <PenTool className="h-4 w-4 text-emerald-600" />
                <span>Cargar Firma Digital Autorizada</span>
              </span>
              <div className="flex items-center gap-1.5 bg-slate-200/60 p-0.5 rounded-lg">
                <button
                  type="button"
                  onClick={() => { setSignatureMode('draw'); setSignatureBase64(''); clearCanvas(); }}
                  className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all cursor-pointer ${signatureMode === 'draw' ? 'bg-white text-slate-800 shadow-3xs' : 'text-slate-550'}`}
                >
                  Dibujar
                </button>
                <button
                  type="button"
                  onClick={() => { setSignatureMode('upload'); setSignatureBase64(''); }}
                  className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all cursor-pointer ${signatureMode === 'upload' ? 'bg-white text-slate-800 shadow-3xs' : 'text-slate-550'}`}
                >
                  Subir Imagen
                </button>
              </div>
            </div>

            {signatureMode === 'draw' ? (
              <div className="space-y-2">
                <p className="text-[10px] text-slate-500 leading-normal">
                  Dibuje la firma del usuario sosteniendo el clic derecho/pulsando sobre el recuadro blanco:
                </p>
                <div className="relative border border-slate-200 rounded-lg bg-white overflow-hidden shadow-3xs">
                  <canvas
                    ref={canvasRef}
                    width={360}
                    height={110}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="w-full block bg-white cursor-crosshair h-[110px]"
                  />
                  
                  <button
                    type="button"
                    onClick={clearCanvas}
                    className="absolute right-2.5 top-2.5 h-6 w-6 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all cursor-pointer"
                    title="Limpiar firma"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] text-slate-550 leading-normal">
                  Cargue la firma (archivo .png, .jpg con fondo transparente de preferencia):
                </p>
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-slate-50/50 transition-all">
                    <div className="flex flex-col items-center justify-center pt-3 pb-3">
                      <Upload className="w-5 h-5 mb-1.5 text-slate-400" />
                      <p className="text-[10px] text-slate-500"><span className="font-bold">Hacer clic</span> para explorar</p>
                    </div>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload} 
                      className="hidden" 
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Signature Preview Thumbnail */}
            {signatureBase64 && (
              <div className="mt-2.5 bg-emerald-50/50 rounded-lg p-2.5 border border-emerald-100 flex items-center justify-between">
                <div>
                  <span className="block text-[9px] font-black uppercase text-emerald-800 tracking-wider">Estado de Firma</span>
                  <span className="block text-[10px] text-slate-600 font-medium mt-0.5">Firma cargada correctamente</span>
                </div>
                <div className="bg-white border border-emerald-200/85 p-1 rounded-md shadow-3xs shrink-0 max-w-[120px]">
                  <img 
                    src={signatureBase64} 
                    alt="Vista previa de firma" 
                    className="max-h-10 object-contain mx-auto" 
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Administrador Toggle (Only visible if the logged in user is admin) */}
        {currentUser?.esAdmin && (
          <div className="bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/60 shadow-2xs space-y-2">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="es-admin-checkbox"
                  type="checkbox"
                  checked={esAdmin}
                  onChange={(e) => setEsAdmin(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </div>
              <div className="ml-2.5 text-xs">
                <label htmlFor="es-admin-checkbox" className="font-bold text-slate-800 cursor-pointer select-none">
                  ¿Asignar Rol de Administrador General?
                </label>
                <p className="text-slate-500 text-[10px] sm:text-xs font-sans mt-0.5 leading-relaxed">
                  Los administradores tienen acceso sin restricciones a todos los módulos y opciones del sistema de forma automática.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Permisos de Módulos (solo habilitados si no es admin) */}
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
            Módulos Autorizados para Acceso
          </label>
          <div className="grid grid-cols-1 gap-2">
            {[
              { id: 'registro_ordenes', title: '1. Registro de Órdenes', desc: 'Crear, ver y cerrar órdenes de producción' },
              { id: 'disposicion_productos', title: '2. Liberación de Calidad', desc: 'Realizar auditoría física, aprobar y rechazar unidosis' },
              { id: 'dashboard_productividad', title: '3. Dashboard de Rendimiento', desc: 'Ver gráficos de indicadores y productividad general' },
              { id: 'gestion_usuarios', title: '4. Gestión de Usuarios', desc: 'Habilitar, desactivar y administrar personal' }
            ].map((module) => {
              const isChecked = esAdmin || modulosPermitidos.includes(module.id);
              const handleModuleToggle = () => {
                if (esAdmin) return;
                setModulosPermitidos(prev => 
                  prev.includes(module.id) 
                    ? prev.filter(id => id !== module.id) 
                    : [...prev, module.id]
                );
              };

              return (
                <div
                  key={module.id}
                  onClick={handleModuleToggle}
                  className={`p-2.5 rounded-lg border transition-all text-left select-none ${
                    esAdmin
                      ? 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
                      : isChecked
                        ? 'bg-emerald-50/40 border-emerald-500/20 cursor-pointer hover:bg-emerald-50/60'
                        : 'bg-white border-slate-200 cursor-pointer hover:bg-slate-50/60'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={esAdmin}
                      onChange={() => {}} // Controlled value handled by clicking container
                      className="h-3.5 w-3.5 mt-0.5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer disabled:opacity-60"
                    />
                    <div className="ml-2.5">
                      <span className={`block text-xs font-bold ${isChecked ? 'text-slate-800' : 'text-slate-500'}`}>
                        {module.title}
                      </span>
                      <span className="block text-[10px] text-slate-400 mt-0.5 leading-snug">
                        {module.desc}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dynamic rule feedback on the fly */}
        {previewUsername && (
          <div className="bg-slate-50/80 p-3 rounded-lg border border-slate-100 space-y-1">
            <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wide">
              Vista previa del Acceso
            </span>
            <div className="flex justify-between items-center text-xs font-sans text-slate-650">
              <span>Usuario generado:</span>
              <span className="font-mono font-bold text-emerald-600">{previewUsername}</span>
            </div>
            <div className="flex justify-between items-center text-xs font-sans text-slate-650">
              <span>Contraseña inicial:</span>
              <span className="font-mono text-slate-705">{cedula || '[Espera de Cédula]'}</span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="w-full h-10 bg-[#1C4F8A] hover:bg-[#071A36] active:bg-[#0D274F] text-white font-sans text-sm font-bold rounded-lg transition-colors shadow-sm cursor-pointer mt-2"
          id="btn-create-user-submit"
        >
          Crear Usuario
        </button>
      </form>
    </div>
  );
}
